import React, { useEffect, useState } from "react";
import styled from "@emotion/styled/macro";
import { useTranslation } from "react-i18next";
import { BodyContentLayout, ContentLayout } from "../../components/ui/ContentLayout";
import arrow from "../../assets/images/arrow.svg";
import { withProps } from "recompose";
import { Accordion, AccordionItem, AccordionButton, AccordionPanel } from "@chakra-ui/react";
import close from "../../assets/images/close.svg";
import CardView from "../../components/pageParts/Cards/CardView";
import CmsService from "../../services/CmsService";
import ReactMarkdown from "react-markdown";

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.powder};
  display: flex;
  flex-direction: column;
  position: relative;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0;
  }
`;

const CardWrapper = styled.div`
  margin: 4em 0;
`;
const StyledContentLayout = styled(ContentLayout)`
  z-index: 1;
`;
const StyledBodyContentLayout = styled(BodyContentLayout)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 2em;
  }
`;
const Title = styled.p`
  padding-top: 1em;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.primary};
`;
const MobileSubtitle = styled.p`
  padding-top: 1em;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.primary};
  padding-bottom: 1em;
  border-bottom: 2px solid ${({ theme }) => theme.colors.opal};
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;
const Subtitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.primary};
`;

const NavMenu = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-right: 3em;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-bottom: 3em;
  }
`;

const FaqContainer = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    padding: 4em 0;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
  }
`;

const DetailsMenu = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0 -2em;
    background-color: ${({ theme }) => theme.colors.white};
    padding: 2em;
  }
`;
const FAQWrapper = styled.div``;

const MenuItem = styled.li`
  list-style: none;
  cursor: pointer;
  padding-bottom: 0.5em;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-top: 1em;
    border-bottom: 1px solid ${({ theme }) => theme.colors.opal};
  }
`;

const StyledAccordionButton = styled(AccordionButton)`
  text-align: left;
  padding: 1em 0;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.secondary};
  justify-content: space-between;
  &:focus {
    box-shadow: none;
  }
`;
const StyledAccordionPanel = styled(AccordionPanel)`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.secondary};
  line-height: 1.5em;
  padding-left: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.opal};
  ul {
    list-style-type: circle;
    padding: 1em;
  }
`;

const StyledAccordionItem = styled(AccordionItem)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.opal};
  border-top: none;
  & a {
    text-decoration: underline;
  }
`;
const StyledAccordion = styled(Accordion)`
  margin-bottom: 3em;
`;
const CardBackground = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 23.75em;
    position: absolute;
    bottom: 0;
    width: 100%;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 76em;
    position: absolute;
    bottom: 0;
    width: 100%;
  }
  background: ${({ theme }) => theme.colors.aero};
`;

const Arrow = withProps({ src: arrow })(styled.img`
  margin: 0 0.5em;
`);
const Close = withProps({ src: close })(styled.img`
  margin: 0 0.5em;
`);
const Faq = () => {
  const { t } = useTranslation();

  const scroll = (scrollTo) => {
    document.querySelector(`#${scrollTo}`)?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const { i18n } = useTranslation();
  const [page, setPage] = useState();
  const { language } = i18n;

  useEffect(() => {
    const fetchPage = async () => {
      const api = new CmsService();
      setPage(null);
      try {
        const response = await api.getPageCollectionTypes(`faqs?populate[categories][populate][0]=questions`);
        setPage(response.categories);
      } catch (e) {
        console.log(e);
      }
    };
    fetchPage();
  }, [language]);

  const cards = ["insurance", "abroad", "accident"];
  const idConverter = /[\s!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g;
  return (
    <Container>
      <StyledBodyContentLayout>
        <Title>{t("faq.title")}</Title>
        <MobileSubtitle>{t("faq.subtitle")}</MobileSubtitle>
        <FaqContainer>
          <NavMenu>
            {page?.map((categories) => (
              <MenuItem onClick={() => scroll(categories?.name.replace(idConverter, ""))}>
                <ReactMarkdown children={categories.name} />
              </MenuItem>
            ))}
          </NavMenu>
          <DetailsMenu>
            {page?.map((categories) => (
              <FAQWrapper id={categories.name?.replace(idConverter, "")}>
                <Subtitle>
                  <ReactMarkdown children={categories.name} />
                </Subtitle>
                <StyledAccordion allowToggle>
                  {categories?.questions.map((question) => (
                    <StyledAccordionItem>
                      {({ isExpanded }) => (
                        <>
                          <StyledAccordionButton>
                            <ReactMarkdown children={question.question} />
                            {isExpanded ? <Close /> : <Arrow />}
                          </StyledAccordionButton>
                          <StyledAccordionPanel>
                            <ReactMarkdown children={question.answer} />
                          </StyledAccordionPanel>
                        </>
                      )}
                    </StyledAccordionItem>
                  ))}
                </StyledAccordion>
              </FAQWrapper>
            ))}
          </DetailsMenu>
        </FaqContainer>
      </StyledBodyContentLayout>
      <StyledContentLayout>
        <CardWrapper>
          <CardView cards={cards} />
        </CardWrapper>
      </StyledContentLayout>
      <CardBackground />
    </Container>
  );
};

export default Faq;
